import type { SignInProvider, SignInProviderId } from "../lib/types";

/**
 * "Continue with …" buttons. Each is an ordinary link: the Worker answers it with a redirect to
 * the provider, and the provider sends the browser back to /api/auth/oauth/<id>/callback, which
 * lands on a two-factor screen exactly as a password does.
 */

/** The providers' own marks, as their sign-in branding guidelines ask for. */
export function ProviderMark({ id }: { id: SignInProviderId }) {
  const common = { width: 18, height: 18, "aria-hidden": true as const, focusable: false as const };
  switch (id) {
    case "github":
      return (
        <svg viewBox="0 0 16 16" {...common} fill="currentColor">
          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
        </svg>
      );
    case "google":
      return (
        <svg viewBox="0 0 48 48" {...common}>
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
      );
    case "microsoft":
      return (
        <svg viewBox="0 0 21 21" {...common}>
          <rect x="1" y="1" width="9" height="9" fill="#F25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
          <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
          <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
        </svg>
      );
    case "linkedin":
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect width="24" height="24" rx="4" fill="#0A66C2" />
          <path fill="#fff" d="M7.1 9.5h2.5v8H7.1zm1.25-4a1.45 1.45 0 110 2.9 1.45 1.45 0 010-2.9zM11 9.5h2.4v1.1h.03c.33-.63 1.15-1.3 2.37-1.3 2.53 0 3 1.67 3 3.84v4.36h-2.5v-3.86c0-.92-.02-2.1-1.28-2.1-1.28 0-1.48 1-1.48 2.04v3.92H11z" />
        </svg>
      );
  }
}

export function startPath(id: SignInProviderId, query: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
  const qs = params.toString();
  return `/api/auth/oauth/${id}/start${qs ? `?${qs}` : ""}`;
}

export function SignInProviders({
  providers, query = {}, verb = "Continue with",
}: {
  providers: SignInProvider[];
  query?: Record<string, string | undefined>;
  verb?: string;
}) {
  if (!providers.length) return null;
  return (
    <div className="provider-list">
      {providers.map((p) => (
        <a key={p.id} className="btn btn-outline btn-block provider-btn" href={startPath(p.id, query)}>
          <ProviderMark id={p.id} />
          <span>{verb} {p.label}</span>
        </a>
      ))}
    </div>
  );
}

/** What the callback's `?oauth_error=` means, in the learner's terms. */
export function oauthErrorMessage(code: string | null): string | null {
  switch (code) {
    case null:
    case "":
      return null;
    case "cancelled":
      return "Sign-in was cancelled.";
    case "expired":
      return "That sign-in took too long, or was finished in a different browser. Please try again.";
    case "provider_failed":
      return "The provider did not complete the sign-in just now. Please try again.";
    case "not_linked":
      return "No account is linked to that sign-in. If you already have an account, sign in with your email and "
        + "password, then link it from your Account page. If you have an invitation, use the link in it.";
    case "invite_invalid":
      return "That invitation is not valid any more. Ask for a new one.";
    case "disabled":
      return "This account has been disabled.";
    case "already_linked":
      return "That account is already linked to a different account here.";
    case "provider_already_linked":
      return "You already have an account from that provider linked. Unlink it first to link a different one.";
    case "link_signin_required":
      return "Sign in first, then link the account from your Account page.";
    case "rate_limited":
      return "Too many attempts. Wait a few minutes and try again.";
    case "unavailable":
      return "That sign-in option is not available.";
    default:
      return "Sign-in did not work. Please try again.";
  }
}
